// Shared name normalisation for the grocery list.
//
// Three things need to agree on "is this the same ingredient?": matching a
// recipe line against items already on the list (recipe.ts), guessing a
// category for it (autocategory.ts), and the add bar's "already on your list"
// suggestions (search.ts). They used to do their own ad-hoc lowercasing, which
// is why "mjölk" didn't match "mjolk" and "Tomater" didn't match "tomat".
//
// Language-agnostic by design: the list is mostly English but Swedish (and
// other Latin-script) names sit in the same household, and nothing here knows
// which language a token is in. Diacritics are folded, a generic Latin
// plural/definite stemmer is applied, prep words in both languages are
// stripped, and finally the EN↔SV grocery dictionary in synonyms.ts maps every
// spelling of one thing onto one canonical key — so "Vitlök" and "garlic" are
// not merely near, they are the same key. Non-Latin scripts are out of scope.
//
// Pure functions, no framework — unit-tested in normalize.test.ts.

import { SYNONYMS } from './synonyms'

// Words that describe how an ingredient is prepared rather than what it is.
// "1 gul lök, finhackad" belongs on the shopping list as "gul lök"; "2 cloves
// garlic, minced" as "garlic". Stored pre-folded (see `fold`), so add new ones
// without diacritics. Deliberately NOT here: "ground"/"whole" — "ground
// beef" and "whole milk" are the things to buy, not prep instructions.
const PREP_WORDS = new Set([
  // Swedish
  'finhackad', 'finhackade', 'grovhackad', 'grovhackade', 'hackad', 'hackade',
  'riven', 'rivet', 'rivna', 'skivad', 'skivade', 'tarnad', 'tarnade',
  'strimlad', 'strimlade', 'urkarnad', 'urkarnade', 'klyftad', 'klyftade',
  'skalad', 'skalade', 'pressad', 'pressade', 'kokt', 'kokta', 'stekt', 'stekta',
  'mald', 'malen', 'torkad', 'torkade',
  'skoljd', 'skoljda', 'avrunnen', 'avrunna', 'halverad', 'halverade', 'kluven', 'kluvna',
  'benfri', 'benfria', 'skinnfri', 'skinnfria', 'rumsvarm', 'rumsvarma',
  'kall', 'kalla', 'kallt', 'uppvispad', 'uppvispade', 'vispad', 'vispade',
  'smalt', 'smalta', 'rostad', 'rostade', 'stor', 'stora', 'liten', 'sma',
  'mellanstor', 'mellanstora', 'mogen', 'mogna',
  // Note: "färsk"/"färska" (fresh) folds to farsk/farska and is prep noise, but
  // "färs" (mince) folds to "fars" and is an ingredient — never strip that one.
  'farsk', 'farska', 'ekologisk', 'ekologiska', 'valfri', 'valfritt',
  'garna', 'ca', 'cirka', 'valfria',
  // English
  'chopped', 'diced', 'sliced', 'minced', 'grated', 'peeled', 'crushed', 'cubed',
  'shredded', 'melted', 'softened', 'toasted', 'roasted', 'cooked', 'dried', 'finely',
  'roughly', 'freshly', 'thinly', 'divided', 'fresh', 'organic', 'large', 'small',
  'medium', 'ripe', 'optional', 'about', 'approx',
  'seeded', 'deseeded', 'trimmed', 'rinsed', 'drained', 'halved', 'quartered',
  'boneless', 'skinless', 'beaten', 'washed', 'pitted', 'stoned', 'cored', 'stemmed',
  'torn', 'crumbled', 'packed', 'sifted', 'heaped', 'heaping', 'thawed', 'defrosted',
  'zested', 'juiced', 'chilled', 'cold', 'warm', 'lightly', 'coarsely', 'extra',
  // German (folded: "gründlich" → grundlich, "große" → grosse)
  'etwas', 'frisch', 'frische', 'frischer', 'gehackt', 'gehackte', 'gewaschen', 'gewaschene',
  'grundlich', 'grob', 'fein', 'gewurfelt', 'gewurfelte', 'geschnitten', 'gerieben', 'geriebener',
  'geschalt', 'geschalte', 'gross', 'grosse', 'grosser', 'klein', 'kleine', 'kleiner', 'zimmerwarm',
])

// Words that only ever open a comma tail ("chicken breast, cut into cubes",
// "salt, plus more for the water", "olive oil, for frying"). Not prep words
// themselves — "cut" at the start of a name is a thing to buy (cut flowers) —
// so they're consulted only at the head of a tail.
const TAIL_STARTERS = new Set([
  'cut', 'plus', 'preferably', 'ideally', 'for', 'to',
  'skuren', 'skurna', 'delad', 'delade', 'helst', 'till', 'for', 'att', 'som',
])

// Multi-word tails that mean the same thing ("… efter smak", "… to taste").
const PREP_PHRASES = [
  'efter smak', 'efter tycke', 'till servering', 'att servera', 'till stekning',
  'till garnering', 'i rumstemperatur',
  'to taste', 'for serving', 'for garnish', 'as needed', 'for frying', 'for the pan',
  'for greasing', 'for drizzling', 'for dusting', 'to serve', 'room temperature',
  'nach geschmack', 'nach belieben', 'zum servieren', 'zum braten',
]

// Joiners that may sit between prep words in a tail ("seeded and diced",
// "peeled, then sliced") without making it name a second ingredient.
const CONJUNCTIONS = new Set(['and', 'or', 'och', 'eller', '&', 'then', 'sedan'])

// Endings a generic Latin stemmer strips — Swedish plural/definite forms plus
// the English/German tails that behave the same way. Longest first. Applied at
// most once per token after the plural step, and never when it would leave a
// stub shorter than MIN_STEM — so "ost" stays "ost" and can't be confused with
// a stemmed longer word.
const SUFFIXES = ['arna', 'orna', 'erna', 'ade', 'ar', 'er', 'or', 'en', 'na', 'ad', 'a', 'e']
const MIN_STEM = 3

// Letters NFD leaves whole: no combining mark to strip, so they need a map of
// their own. Applied after lowercasing, so the uppercase forms are covered.
const TRANSLITERATE: Record<string, string> = {
  'ø': 'o', 'æ': 'ae', 'œ': 'oe', 'ß': 'ss', 'ł': 'l', 'đ': 'd', 'ð': 'd', 'þ': 'th',
}

/** Lowercase, strip diacritics, collapse whitespace. "Mjölk " → "mjolk", "Smørrebrød" → "smorrebrod". */
export function fold(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[øæœßłđðþ]/g, (c) => TRANSLITERATE[c] ?? c)
    .replace(/\s+/g, ' ')
    .trim()
}

/** Splits on whitespace, dropping empties. */
export function tokens(s: string): string[] {
  return s.split(/\s+/).filter(Boolean)
}

/**
 * Removes preparation noise while preserving the original casing/diacritics of
 * what's left: parentheticals, comma clauses that are nothing but prep words
 * ("gul lök, finhackad", "boneless, skinless chicken breasts", "red bell
 * pepper, seeded and diced") or that open with one ("chicken breast, cut into
 * cubes", "olive oil, for frying"), and leading/trailing prep words ("färsk
 * spenat", "fresh basil").
 *
 * Every comma clause is judged on its own, so a clause that names a second
 * ingredient survives ("salt, peppar" stays intact for the "two items in one
 * line" split). The head clause is only skipped when it is *all* prep; its
 * leading prep words ("fresh basil, torn", "skinless chicken breasts") are
 * handled by the word loop below, not by throwing the clause away.
 */
export function stripDescriptors(s: string): string {
  let out = s.replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim()

  const clauses = out.split(',').map(c => c.trim()).filter(Boolean)
  if (clauses.length > 1) {
    // Leading clauses that are all prep ("boneless, skinless chicken breasts")
    // describe what follows; the first clause with a name in it is the head.
    let start = 0
    while (start < clauses.length - 1 && isAllPrep(clauses[start])) start++
    const tails = clauses.slice(start + 1).filter(c => !(isAllPrep(c) || startsWithPrep(c)))
    out = [clauses[start], ...tails].join(', ')
  }

  // "salt efter smak", "parsley to serve" — the phrase tails also come without
  // a comma. Cut by word count so the original casing stays.
  let parts = tokens(out)
  for (const phrase of PREP_PHRASES) {
    const n = tokens(phrase).length
    if (parts.length > n && fold(parts.slice(-n).join(' ')) === phrase) {
      parts = parts.slice(0, -n)
      break
    }
  }
  while (parts.length > 1 && PREP_WORDS.has(fold(parts[0]))) parts = parts.slice(1)
  while (parts.length > 1 && PREP_WORDS.has(fold(parts[parts.length - 1]))) parts = parts.slice(0, -1)

  const result = parts.join(' ')
  // Never strip a name down to nothing — "riven" alone stays "riven".
  return result || s.trim()
}

/** True when the text is nothing but prep words ("fresh", "finely chopped", "seeded and diced", "efter smak"). */
export function isAllPrep(s: string): boolean {
  const folded = fold(s)
  if (!folded) return false
  if (PREP_PHRASES.includes(folded)) return true
  const words = tokens(folded).map(t => t.replace(/[.]$/, ''))
  return words.some(t => PREP_WORDS.has(t)) && words.every(t => PREP_WORDS.has(t) || CONJUNCTIONS.has(t))
}

function startsWithPrep(s: string): boolean {
  const first = tokens(fold(s))[0]?.replace(/[.]$/, '')
  return first !== undefined && (PREP_WORDS.has(first) || TAIL_STARTERS.has(first))
}

/**
 * Strips a plural/definite ending without knowing the language. Two steps,
 * each refusing to leave fewer than MIN_STEM letters:
 *
 *   1. the English plural — "ies" → "ie", "sses" → "ss", else a trailing "s"
 *      unless it follows s/u/i ("glass", "hummus", and Swedish -is singulars
 *      like "gris"/"potatis" stay whole);
 *   2. "y" → "i", then one ending from SUFFIXES.
 *
 * The stems are internal keys, never shown, so "chicken" → "chick" is fine —
 * what matters is that every spelling of one thing lands on the same key:
 * "tomater"/"tomaterna"/"tomat" → "tomat", "tomatoes"/"tomato" → "tomato",
 * "berries"/"berry" → "berri", "peppers"/"pepper" → "pepp".
 */
export function stemToken(t: string): string {
  let s = t
  if (s.length - 1 >= MIN_STEM) {
    if (s.endsWith('ies')) s = s.slice(0, -1)
    else if (s.endsWith('sses')) s = s.slice(0, -2)
    else if (s.endsWith('s') && !/[sui]s$/.test(s)) s = s.slice(0, -1)
  }
  if (s.length - 1 >= MIN_STEM && s.endsWith('y')) s = s.slice(0, -1) + 'i'
  for (const suffix of SUFFIXES) {
    if (s.length - suffix.length >= MIN_STEM && s.endsWith(suffix)) {
      return s.slice(0, -suffix.length)
    }
  }
  return s
}

// Descriptors stripped, folded, every token stemmed — the key before the
// dictionary. The dictionary itself is built with this, so its spellings are
// treated exactly like names.
// Commas and the like are separators, not part of a word: a kept tail
// ("potatoes, waxy") must not leave "potatoes," as a token of its own.
function rawKeyTokens(s: string): string[] {
  const folded = fold(stripDescriptors(s)).replace(/[,;:]/g, ' ')
  if (!folded.trim()) return []
  return tokens(folded).map(stemToken)
}

/**
 * The canonical key for "is this the same ingredient?": descriptors stripped,
 * diacritics folded, every token stemmed, then every spelling in synonyms.ts
 * replaced by its group's canonical one (longest phrase first).
 *
 *   "Krossade tomater"      → "kross tomato"
 *   "1 gul lök, finhackad"  → "1 yellow onion"   (amounts are parsed off upstream)
 *   "Mjölk" / "mjolk" / "milk" → "milk"
 *   "Olivolja" / "olive oil"   → "oliv oil"
 *   "Cherry tomatoes"       → "cherri tomato"
 */
export function nameKey(s: string): string {
  return applySynonyms(rawKeyTokens(s)).join(' ')
}

/**
 * A key's words in a fixed order: "fast potatis" and "potatis fast" (or
 * "potatoes, waxy" and "waxy potatoes") are one thing written two ways. This is
 * the identity for "one entry per name" — the catalog, the category memory, a
 * recipe set's shared ingredients. Matching still uses the ordered nameKey,
 * because `nearness` reads the last word as the head noun; it treats two keys
 * with the same words as near (see `sameWords`).
 */
export function wordsKey(key: string): string {
  return tokens(key).sort().join(' ')
}

/** `wordsKey` of a name rather than of a key. */
export const itemKey = (name: string): string => wordsKey(nameKey(name))

/** Same words, any order — "fast potato" / "potato fast". */
export function sameWords(a: string, b: string): boolean {
  return !!a && wordsKey(a) === wordsKey(b)
}

// ── synonyms ─────────────────────────────────────────────────────────────────

// Raw key of every spelling → raw-key tokens of its group's canonical spelling.
// Built once at module load. Multi-word spellings ("gul lök", "olive oil") are
// matched as a window over the token list, longest window first, so "gul lok"
// becomes "yellow onion" rather than "gul onion".
const SYNONYM_KEYS = new Map<string, string[]>()
let MAX_SYNONYM_TOKENS = 1
for (const group of SYNONYMS) {
  const canonical = rawKeyTokens(group[0])
  for (const spelling of group) {
    const key = rawKeyTokens(spelling)
    if (key.length === 0) continue
    MAX_SYNONYM_TOKENS = Math.max(MAX_SYNONYM_TOKENS, key.length)
    SYNONYM_KEYS.set(key.join(' '), canonical)
  }
}

// Single-word spellings, for the typo fallback below.
const SINGLE_SYNONYM_KEYS = [...SYNONYM_KEYS].filter(([k]) => !k.includes(' '))

function applySynonyms(toks: string[]): string[] {
  const out: string[] = []
  let i = 0
  while (i < toks.length) {
    let matched = false
    for (let n = Math.min(MAX_SYNONYM_TOKENS, toks.length - i); n >= 1; n--) {
      const hit = SYNONYM_KEYS.get(toks.slice(i, i + n).join(' '))
      if (hit) {
        out.push(...hit)
        i += n
        matched = true
        break
      }
    }
    if (!matched) {
      out.push(...(typoSynonym(toks[i]) ?? [toks[i]]))
      i++
    }
  }
  return out
}

// "mjolkk" is a typo of "mjolk", and without this it would sit one letter from
// "mjolk" while the correctly spelt item had moved on to "milk" — the dictionary
// would have made a typo *worse*. So a token that is no spelling but one edit
// from a single-word one takes that word's group. Stricter than `nearness`:
// one edit only, five letters or more, same first letter, and never the prefix
// rule — rewriting a key is a bigger step than calling two keys near, and
// "hallouni" must stay a halloumi typo rather than become raspberries ("hallon").
// One letter more at the end is a compound, not a slip — "gradds" (gräddsås, cream
// sauce) is no "gradd" (grädde, cream) — unless it doubles the last ("mjolkk").
function typoSynonym(tok: string): string[] | null {
  if (tok.length < 5) return null
  for (const [key, canonical] of SINGLE_SYNONYM_KEYS) {
    if (key[0] !== tok[0] || Math.abs(key.length - tok.length) > 1) continue
    if (tok.length === key.length + 1 && tok.startsWith(key) && tok[tok.length - 1] !== key[key.length - 1]) continue
    if (levenshtein(tok, key) <= 1) return canonical
  }
  return null
}

/** The canonical raw key a spelling maps to, or null when it is in no group. Exposed for synonyms.test.ts. */
export function synonymKey(spelling: string): string | null {
  const hit = SYNONYM_KEYS.get(rawKeyTokens(spelling).join(' '))
  return hit ? hit.join(' ') : null
}

// ── nearness ─────────────────────────────────────────────────────────────────

/**
 * How far apart two keys are, or null when they aren't close enough to be the
 * same thing. The one definition of "close enough", shared by the recipe
 * importer's merge, "what am I missing" and the add bar's suggestions.
 *
 * Whole keys first: tolerance grows with length (a one-letter slip in "mjolk"
 * is a typo; in "ris" it's a different word), and one key being a prefix of the
 * other counts too ("tomat" / "tomatpure") once there are four letters to go on.
 *
 * Then token by token: a shorter key is near a longer one when its last token
 * — the head noun, last in English and Swedish alike — is near the longer key's
 * last token and each of its other tokens is near some token of the longer key
 * ("cherri tomato" / "tomato", "bell pepp" / "red bell pepp", "kross tomat" /
 * "tomat"). Keys with the same number of tokens never take this route: "oliv
 * oil" / "sesame oil" and "red pepp" / "green pepp" are different things, and a
 * false "same" here becomes a wrong merge or a missing ingredient reported as
 * covered. Each extra token costs more than a whole-key distance does, so a
 * whole-key match (typo or compound) ranks above a token-subset one.
 */
export function nearness(a: string, b: string): number | null {
  if (!a || !b) return null
  if (a === b) return 0
  // The same words in another order — closer than any typo, never than equal.
  if (sameWords(a, b)) return 1
  const whole = wholeNearness(a, b)
  if (whole !== null) return whole
  return subsetNearness(tokens(a), tokens(b))
}

function wholeNearness(a: string, b: string): number | null {
  if (a === b) return 0
  const typo = typoNearness(a, b)
  if (typo !== null) return typo
  const minLen = Math.min(a.length, b.length)
  if (minLen >= 4 && (a.startsWith(b) || b.startsWith(a))) return Math.max(a.length, b.length) - minLen
  return null
}

// Typo tolerance, token by token: a slip in one word is a typo, a different
// qualifier is not — "red pepp" / "gre pepp" are two edits apart as strings but
// name two vegetables, and measured per token "red" / "gre" get no tolerance.
function typoNearness(a: string, b: string): number | null {
  const ta = tokens(a)
  const tb = tokens(b)
  if (ta.length !== tb.length) return null
  if (ta.length === 1) return typoDistance(a, b)
  let sum = 0
  for (let i = 0; i < ta.length; i++) {
    const d = tokenNearness(ta[i], tb[i])
    if (d === null) return null
    sum += d
  }
  return sum
}

// Edit distance when it is within what a typo could explain, else null.
function typoDistance(a: string, b: string): number | null {
  const maxLen = Math.max(a.length, b.length)
  const tol = maxLen >= 8 ? 2 : maxLen >= 5 ? 1 : 0
  if (Math.abs(a.length - b.length) > tol) return null
  const dist = levenshtein(a, b)
  return dist <= tol ? dist : null
}

// More than any whole-key distance in practice (a typo is ≤2, a compound's
// extra letters rarely reach ten), so "parmesan chees" still prefers the item
// "Parmesan" over "Cheese".
const EXTRA_TOKEN_COST = 10

function subsetNearness(a: string[], b: string[]): number | null {
  if (a.length === b.length || a.length === 0 || b.length === 0) return null
  const [short, long] = a.length < b.length ? [a, b] : [b, a]
  const head = tokenNearness(short[short.length - 1], long[long.length - 1])
  if (head === null) return null
  let sum = head
  for (const t of short.slice(0, -1)) {
    let best: number | null = null
    for (const u of long.slice(0, -1)) {
      const d = tokenNearness(t, u)
      if (d !== null && (best === null || d < best)) best = d
    }
    if (best === null) return null
    sum += best
  }
  return EXTRA_TOKEN_COST * (long.length - short.length) + sum
}

// Short tokens ("&", "of", "av") only ever match themselves.
function tokenNearness(a: string, b: string): number | null {
  if (a.length < 3 || b.length < 3) return a === b ? 0 : null
  return wholeNearness(a, b)
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0
  if (!a.length) return b.length
  if (!b.length) return a.length
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i++) {
    const cur = [i]
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost)
    }
    prev = cur
  }
  return prev[b.length]
}
